import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/profile-components/Navbar";
import Topbar from "../components/profile-components/Topbar";
import "./SalesPages.css";
import { authFetch } from "../utils/authFetch";

function ReportsPage() {
  const [templates, setTemplates] = useState([]);
  const [savedReports, setSavedReports] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [savedReportsLoading, setSavedReportsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadTemplates();
    loadSavedReports();
  }, []);

  const loadTemplates = async () => {
    try {
      setTemplatesLoading(true);

      const res = await authFetch(`/api/reports/templates`);

      if (!res.ok) {
        throw new Error("Failed to load report templates");
      }

      const data = await res.json();
      setTemplates(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Load report templates error:", err);
      setTemplates([]);
    } finally {
      setTemplatesLoading(false);
    }
  };

  const loadSavedReports = async () => {
    try {
      setSavedReportsLoading(true);

      const res = await authFetch(`/api/reports/saved`);

      if (!res.ok) {
        throw new Error("Failed to load saved reports");
      }

      const data = await res.json();
      setSavedReports(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Load saved reports error:", err);
      setSavedReports([]);
    } finally {
      setSavedReportsLoading(false);
    }
  };

  const recentSavedReports = useMemo(
    () => savedReports.slice(0, 5),
    [savedReports]
  );

  const getDatasetLabel = (dataset) => {
    switch (dataset) {
      case "clients":
        return "Clients";
      case "policies":
        return "Policies";
      case "opportunities":
        return "Opportunities";
      case "leads":
        return "Leads";
      case "tasks":
        return "Tasks";
      default:
        return dataset || "Report";
    }
  };

  const formatDateTime = (value) => {
    if (!value) return "-";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return "-";

    return date.toLocaleString();
  };

  const jumpToSavedReports = () => {
    const section = document.getElementById("saved-reports-section");

    if (section) {
      section.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <>
      <Navbar />
      <Topbar />

      <div className="main-content sales-page">
        <div className="sales-hero sales-profile-hero">
          <div>
            <p className="sales-eyebrow">Agent Book CRM</p>
            <h1 className="sales-title">Reports</h1>
            <p className="sales-subtitle">
              Run, save, export, and reuse default reports to analyze production
              and cross-sell opportunities.
            </p>
          </div>

          <div className="sales-header-actions">
            <button
              type="button"
              className="sales-secondary-btn"
              onClick={jumpToSavedReports}
            >
              Saved Reports
            </button>
          </div>
        </div>

        <div className="sales-table-card" style={{ marginBottom: "12px" }}>
          <div className="sales-table-header">
            <div>
              <h2>Available Report Templates</h2>
              <p>
                {templatesLoading
                  ? "Loading reports..."
                  : `${templates.length} report template${
                      templates.length === 1 ? "" : "s"
                    }`}
              </p>
            </div>
          </div>

          <div className="sales-table-wrap">
            <table className="sales-table">
              <thead>
                <tr>
                  <th>Report Name</th>
                  <th>Primary Dataset</th>
                  <th>Description</th>
                  <th style={{ width: "160px" }}>Actions</th>
                </tr>
              </thead>

              <tbody>
                {templatesLoading ? (
                  <tr>
                    <td colSpan="4" className="sales-empty-cell">
                      Loading report templates...
                    </td>
                  </tr>
                ) : templates.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="sales-empty-cell">
                      No report templates available.
                    </td>
                  </tr>
                ) : (
                  templates.map((template) => (
                    <tr key={template.id}>
                      <td>
                        <div className="sales-primary-cell">
                          <div className="sales-name">{template.name}</div>
                        </div>
                      </td>

                      <td>{getDatasetLabel(template.primary_dataset)}</td>
                      <td>
                        {template.description || "No description available."}
                      </td>

                      <td>
                        <button
                          className="sales-secondary-btn"
                          onClick={() =>
                            navigate(`/reports/${template.template_key}`)
                          }
                        >
                          Run Report
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="sales-table-card" id="saved-reports-section">
          <div className="sales-table-header">
            <div>
              <h2>Recent Saved Reports</h2>
              <p>
                {savedReportsLoading
                  ? "Loading saved reports..."
                  : `${savedReports.length} saved report${
                      savedReports.length === 1 ? "" : "s"
                    } total`}
              </p>
            </div>
          </div>

          <div className="sales-table-wrap">
            <table className="sales-table">
              <thead>
                <tr>
                  <th>Report Name</th>
                  <th>Template</th>
                  <th>Last Updated</th>
                  <th style={{ width: "180px" }}>Actions</th>
                </tr>
              </thead>

              <tbody>
                {savedReportsLoading ? (
                  <tr>
                    <td colSpan="4" className="sales-empty-cell">
                      Loading saved reports...
                    </td>
                  </tr>
                ) : recentSavedReports.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="sales-empty-cell">
                      No saved reports yet.
                    </td>
                  </tr>
                ) : (
                  recentSavedReports.map((report) => (
                    <tr key={report.id}>
                      <td>
                        <div className="sales-primary-cell">
                          <div className="sales-name">{report.name}</div>
                        </div>
                      </td>

                      <td>{report.template_name || report.template_key || "-"}</td>
                      <td>{formatDateTime(report.updated_at)}</td>

                      <td>
                        <button
                          className="sales-secondary-btn"
                          onClick={() =>
                            navigate(
                              `/reports/${report.template_key}?savedReportId=${report.id}`
                            )
                          }
                        >
                          Open Report
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

export default ReportsPage;